import { SerializedMesh, SerializedPaintedArea, StatsMap } from '@/workers/viewAnalysis.worker';
import * as THREE from 'three';
import { useViewFeaturesStore } from '@/store/viewFeaturesStore';
import {
	SelectedViewPlaneStats,
	ViewPlaneBlockingMode,
	ViewPlaneVisualizationMode,
	useThreeSceneStore,
} from '@/store/threeSceneStore';

const VIEW_HORIZONTAL_FOV_DEG = 120;
const VIEW_START_PITCH_DEG = -30;
const VIEW_END_PITCH_DEG = 30;
const DEFAULT_PLANE_DISTANCE = 20;
const MIN_PLANE_DISTANCE = 1;
const HEATMAP_BLOCK_FALLOFF_METERS = 50;

export interface PlaneRayDetail {
	localX: number;
	localY: number;
	category: string;
	cssColor: string;
	hitDistance: number;
	blocked: boolean;
	distanceScore: number;
}

const toRgbString = (r: number, g: number, b: number) => {
	return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
};

interface PerformScanParams {
	scene: THREE.Scene;
	camera: THREE.Camera;
	originPoint: THREE.Vector3;
	viewDirection?: THREE.Vector3;
	targets: THREE.Object3D[];
	setDebugGeo: (geo: THREE.BufferGeometry) => void;
	setAnalysisData: (data: Record<string, StatsMap>) => void;
	categoryFilter?: Set<string>;
	onProgress?: (progress: number) => void; // Optional progress callback
	windowId?: string; // Unique identifier for the window being analyzed
}

const serializeMesh = (mesh: THREE.Mesh): SerializedMesh => {
	mesh.updateMatrixWorld(true);

	const geometry = mesh.geometry;
	const posAttr = geometry.getAttribute('position');
	const uvAttr = geometry.getAttribute('uv');
	const indexAttr = geometry.index;

	const vertices: number[] = [];
	for (let i = 0; i < posAttr.count; i++) {
		vertices.push(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
	}

	const indices: number[] = [];
	if (indexAttr) {
		for (let i = 0; i < indexAttr.count; i++) {
			indices.push(indexAttr.getX(i));
		}
	} else {
		for (let i = 0; i < posAttr.count; i++) {
			indices.push(i);
		}
	}

	const category = mesh.userData.category || mesh.userData.type || 'unknown';

	// Check material transparency
	let isTransparent = mesh.userData.isTransparent || false;
	let isWindow = mesh.userData.isWindow || false;

	const material = mesh.material;
	if (material && !Array.isArray(material)) {
		const mat = material as THREE.MeshStandardMaterial;
		// Check if material is actually transparent
		if (mat.transparent && mat.opacity < 0.99) {
			isTransparent = true;
		}
		// Check if it's a window-like material (transparent + high transmission)
		if (
			mat instanceof THREE.MeshPhysicalMaterial &&
			mat.transparent &&
			mat.transmission &&
			mat.transmission > 0.5
		) {
			isWindow = true;
			isTransparent = true;
		}
	}

	// Serialize UVs if available
	let uvs: number[] | undefined;
	if (uvAttr) {
		uvs = [];
		for (let i = 0; i < uvAttr.count; i++) {
			uvs.push(uvAttr.getX(i), uvAttr.getY(i));
		}
	}

	// Serialize texture color data for terrain ONLY
	let colorData: Uint8ClampedArray | undefined;
	let colorDataWidth: number | undefined;
	let colorDataHeight: number | undefined;

	if (category === 'terrain') {
		if (material && !Array.isArray(material)) {
			const mat = material as THREE.MeshStandardMaterial;
			if (mat.map && mat.map.image) {
				const image = mat.map.image;
				try {
					const canvas = document.createElement('canvas');
					if (image instanceof HTMLCanvasElement) {
						canvas.width = image.width;
						canvas.height = image.height;
						const ctx = canvas.getContext('2d');
						if (ctx) {
							ctx.drawImage(image, 0, 0);
							colorData = ctx.getImageData(0, 0, image.width, image.height).data;
							colorDataWidth = image.width;
							colorDataHeight = image.height;
						}
					} else if (image instanceof HTMLImageElement && image.complete) {
						canvas.width = image.naturalWidth || image.width;
						canvas.height = image.naturalHeight || image.height;
						const ctx = canvas.getContext('2d');
						if (ctx) {
							ctx.drawImage(image, 0, 0);
							colorData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
							colorDataWidth = canvas.width;
							colorDataHeight = canvas.height;
						}
					}
				} catch (error) {
					console.warn('Failed to extract color data from terrain texture:', error);
				}
			}
		}
	}

	return {
		vertices,
		indices,
		uvs,
		colorData,
		colorDataWidth,
		colorDataHeight,
		matrix: mesh.matrixWorld.toArray(),
		userData: {
			category: category,
			type: mesh.userData.type,
			isWindow: isWindow,
			isTransparent: isTransparent,
			isLandmark: mesh.userData.isLandmark || false,
		},
		name: mesh.name || `${category}_mesh`,
	};
};

// Store view lines for multiple windows
const viewLinesMap = new Map<string, THREE.LineSegments>();
const viewSamplingPlaneMap = new Map<string, THREE.Mesh>();

const clearSamplingPlanesExcept = (activeWindowId: string, scene: THREE.Scene) => {
	const staleEntries = Array.from(viewSamplingPlaneMap.entries()).filter(
		([windowId]) => windowId !== activeWindowId
	);
	const staleLineEntries = Array.from(viewLinesMap.entries()).filter(
		([windowId]) => windowId !== activeWindowId
	);

	let shouldResetSelection = false;
	for (const [windowId, plane] of staleEntries) {
		if (useThreeSceneStore.getState().selectedViewPlaneStats?.windowId === windowId) {
			shouldResetSelection = true;
		}
		scene.remove(plane);
		disposeObject3D(plane);
		viewSamplingPlaneMap.delete(windowId);
	}

	for (const [windowId, lines] of staleLineEntries) {
		scene.remove(lines);
		lines.geometry.dispose();
		if (lines.material instanceof THREE.Material) {
			lines.material.dispose();
		} else if (Array.isArray(lines.material)) {
			lines.material.forEach((mat) => mat.dispose());
		}
		viewLinesMap.delete(windowId);
	}

	if (staleEntries.length === 0 && staleLineEntries.length === 0) {
		return;
	}

	if (shouldResetSelection) {
		useThreeSceneStore.getState().setSelectedViewPlaneStats(null);
		useThreeSceneStore.getState().setViewPlaneInspector(null);
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new CustomEvent('__viewAnalysisClearVisualization__'));
		}
	}
};

type PlaneCategoryAccumulator = {
	count: number;
	totalDistance: number;
	color: string;
};

const getCategoryAccumulator = (
	categoryCounts: Map<string, PlaneCategoryAccumulator>,
	category: string,
	color: string
) => {
	const existing = categoryCounts.get(category);
	if (existing) {
		return existing;
	}

	const created = {
		count: 0,
		totalDistance: 0,
		color,
	};
	categoryCounts.set(category, created);
	return created;
};

const applyPlaneVisualizationMode = (
	planeMesh: THREE.Mesh,
	mode: ViewPlaneVisualizationMode = useThreeSceneStore.getState().viewPlaneVisualizationMode
) => {
	const heatmap = planeMesh.getObjectByName('__viewRayPlaneHeatmap__');
	const points = planeMesh.getObjectByName('__viewRayPlanePoints__');

	if (heatmap) {
		heatmap.visible = mode === 'heatmap' || mode === 'both';
	}

	if (points) {
		points.visible = mode === 'dots' || mode === 'both';
	}
};

const getPlaneCategoryFilter = (planeMesh: THREE.Mesh): string | null => {
	const category = planeMesh.userData.categoryFilter;
	return typeof category === 'string' && category.length > 0 ? category : null;
};

const getPlaneBlockingMode = (planeMesh: THREE.Mesh): ViewPlaneBlockingMode => {
	const mode = planeMesh.userData.blockingMode;
	if (mode === 'plane' || mode === 'source') {
		return mode;
	}

	return useThreeSceneStore.getState().viewPlaneBlockingMode;
};

const disposeObject3D = (object: THREE.Object3D) => {
	object.traverse((child) => {
		if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments || child instanceof THREE.Points) {
			if (child.geometry) {
				child.geometry.dispose();
			}
			if (child.material instanceof THREE.Material) {
				child.material.dispose();
			} else if (Array.isArray(child.material)) {
				child.material.forEach((mat) => mat.dispose());
			}
		}
	});
};

const buildPlaneDimensions = (distanceFromOrigin: number) => {
	const distance = Math.max(MIN_PLANE_DISTANCE, distanceFromOrigin);
	const halfHorizontal = THREE.MathUtils.degToRad(VIEW_HORIZONTAL_FOV_DEG / 2);
	const startPitch = THREE.MathUtils.degToRad(VIEW_START_PITCH_DEG);
	const endPitch = THREE.MathUtils.degToRad(VIEW_END_PITCH_DEG);
	const width = 2 * distance * Math.tan(halfHorizontal);
	const height = distance * (Math.tan(endPitch) - Math.tan(startPitch));

	return { width, height };
};

const updatePlaneFrameGeometry = (frame: THREE.LineSegments, width: number, height: number) => {
	const halfW = width / 2;
	const halfH = height / 2;
	const framePositions = new Float32Array([
		-halfW,
		-halfH,
		0,
		halfW,
		-halfH,
		0,
		halfW,
		-halfH,
		0,
		halfW,
		halfH,
		0,
		halfW,
		halfH,
		0,
		-halfW,
		halfH,
		0,
		-halfW,
		halfH,
		0,
		-halfW,
		-halfH,
		0,
	]);

	if (frame.geometry) {
		frame.geometry.dispose();
	}
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute('position', new THREE.BufferAttribute(framePositions, 3));
	frame.geometry = geometry;
};

const updatePlaneSurfaceGeometry = (mesh: THREE.Mesh, width: number, height: number) => {
	if (mesh.geometry) {
		mesh.geometry.dispose();
	}
	mesh.geometry = new THREE.PlaneGeometry(width, height);
};

const updateHeatmapTexture = (
	heatmapMesh: THREE.Mesh,
	width: number,
	height: number,
	allRayPlaneData: number[] // [localX, localY, blockedFlag, blockedDistanceScore]
) => {
	const heatmapCanvas = heatmapMesh.userData.canvas as HTMLCanvasElement | undefined;
	const heatmapTexture = heatmapMesh.userData.texture as THREE.CanvasTexture | undefined;
	if (!heatmapCanvas || !heatmapTexture) {
		return;
	}

	const ctx = heatmapCanvas.getContext('2d');
	if (!ctx) {
		return;
	}

	const canvasWidth = heatmapCanvas.width;
	const canvasHeight = heatmapCanvas.height;
	ctx.clearRect(0, 0, canvasWidth, canvasHeight);

	if (allRayPlaneData.length === 0) {
		heatmapTexture.needsUpdate = true;
		return;
	}

	const gridW = canvasWidth;
	const gridH = canvasHeight;
	const halfW = width / 2;
	const halfH = height / 2;
	const spreadR = Math.max(1, Math.round(Math.min(gridW, gridH) * 0.04));

	// Per-cell weighted accumulation.
	// Open rays are rendered green.
	// Blocked rays are rendered red->yellow (near->far according to current blocking mode).
	const openWeight = new Float32Array(gridW * gridH);
	const blockedWeight = new Float32Array(gridW * gridH);
	const blockedDistanceScoreSum = new Float32Array(gridW * gridH);

	for (let i = 0; i < allRayPlaneData.length; i += 4) {
		const u = (allRayPlaneData[i] + halfW) / width;
		const v = 1 - (allRayPlaneData[i + 1] + halfH) / height;
		const blockedFlag = allRayPlaneData[i + 2] > 0.5;
		const blockedDistanceScore = allRayPlaneData[i + 3];
		const gx = Math.round(u * (gridW - 1));
		const gy = Math.round(v * (gridH - 1));
		for (let dy = -spreadR; dy <= spreadR; dy++) {
			for (let dx = -spreadR; dx <= spreadR; dx++) {
				const nx = gx + dx;
				const ny = gy + dy;
				if (nx >= 0 && nx < gridW && ny >= 0 && ny < gridH) {
					const w = Math.exp(-(dx * dx + dy * dy) / (spreadR * spreadR));
					const idx = ny * gridW + nx;
					if (blockedFlag) {
						blockedWeight[idx] += w;
						blockedDistanceScoreSum[idx] += blockedDistanceScore * w;
					} else {
						openWeight[idx] += w;
					}
				}
			}
		}
	}

	const imageData = ctx.createImageData(gridW, gridH);
	const data = imageData.data;
	for (let gy = 0; gy < gridH; gy++) {
		for (let gx = 0; gx < gridW; gx++) {
			const cellIndex = gy * gridW + gx;
			const o = openWeight[cellIndex];
			const bW = blockedWeight[cellIndex];
			if (o === 0 && bW === 0) {
				continue;
			}

			let r: number;
			let g: number;
			let b: number;
			let alpha: number;

			if (bW === 0 && o > 0) {
				// Total open view for this cell.
				r = 34;
				g = 197;
				b = 94;
				alpha = 0.42;
			} else {
				// Blocked view: red if near blocker, yellow if far blocker.
				const distScore = THREE.MathUtils.clamp(blockedDistanceScoreSum[cellIndex] / bW, 0, 1);
				const colorT = Math.pow(distScore, 0.7);
				r = 255;
				g = Math.round(255 * colorT);
				b = 0;
				const blockedRatio = bW / Math.max(0.001, o + bW);
				alpha = 0.3 + blockedRatio * 0.6;
			}

			const idx = cellIndex * 4;
			data[idx] = r;
			data[idx + 1] = g;
			data[idx + 2] = b;
			data[idx + 3] = Math.round(alpha * 255);
		}
	}
	ctx.putImageData(imageData, 0, 0);
	heatmapTexture.needsUpdate = true;
};

const updateSamplingIntersections = (
	planeMesh: THREE.Mesh,
	origin: THREE.Vector3,
	raySegments: number[],
	raySegmentColors?: number[],
	raySegmentCategories?: string[]
) => {
	const frame = planeMesh.getObjectByName('__viewRayPlaneFrame__') as THREE.LineSegments | undefined;
	const points = planeMesh.getObjectByName('__viewRayPlanePoints__') as THREE.Points | undefined;
	const heatmap = planeMesh.getObjectByName('__viewRayPlaneHeatmap__') as THREE.Mesh | undefined;
	if (!frame || !points || !heatmap) {
		return;
	}

	const baseForwardData = planeMesh.userData.baseForward;
	const baseForward = baseForwardData instanceof THREE.Vector3
		? baseForwardData
		: baseForwardData
			? new THREE.Vector3(baseForwardData.x, baseForwardData.y, baseForwardData.z).normalize()
			: null;

	const fromOrigin = planeMesh.position.clone().sub(origin);
	const forwardDistance = baseForward
		? Math.max(MIN_PLANE_DISTANCE, Math.abs(fromOrigin.dot(baseForward)))
		: Math.max(MIN_PLANE_DISTANCE, fromOrigin.length());

	const { width, height } = buildPlaneDimensions(forwardDistance);
	const halfW = width / 2;
	const halfH = height / 2;
	updatePlaneFrameGeometry(frame, width, height);
	updatePlaneSurfaceGeometry(planeMesh, width, height);
	updatePlaneSurfaceGeometry(heatmap, width, height);

	planeMesh.updateMatrixWorld(true);
	const planeNormal = new THREE.Vector3(0, 0, 1)
		.applyQuaternion(planeMesh.getWorldQuaternion(new THREE.Quaternion()))
		.normalize();
	const worldPosition = planeMesh.getWorldPosition(new THREE.Vector3());
	const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, worldPosition);

	const line = new THREE.Line3();
	const start = new THREE.Vector3();
	const end = new THREE.Vector3();
	const hit = new THREE.Vector3();
	const localHit = new THREE.Vector3();
	// Infinite-ray projection for the heatmap — covers ALL ray directions on the plane.
	const rayObj = new THREE.Ray();
	const rayDir = new THREE.Vector3();
	const infiniteHit = new THREE.Vector3();
	const infiniteLocalHit = new THREE.Vector3();
	const hitPositions: number[] = [];
	const hitColors: number[] = [];
	const allRayPlaneData: number[] = []; // [localX, localY, blockedFlag, blockedDistanceScore]
	const categoryCounts = new Map<string, PlaneCategoryAccumulator>();
	let totalHitDistance = 0;
	const activeCategoryFilter = getPlaneCategoryFilter(planeMesh);
	const activeBlockingMode = getPlaneBlockingMode(planeMesh);

	for (let i = 0; i < raySegments.length; i += 6) {
		const segmentIndex = i / 6;
		start.set(raySegments[i], raySegments[i + 1], raySegments[i + 2]);
		end.set(raySegments[i + 3], raySegments[i + 4], raySegments[i + 5]);
		line.set(start, end);

		const intersection = plane.intersectLine(line, hit);
		if (!intersection) {
			continue;
		}

		localHit.copy(intersection);
		planeMesh.worldToLocal(localHit);
		if (Math.abs(localHit.x) > halfW || Math.abs(localHit.y) > halfH) {
			continue;
		}

		const category = raySegmentCategories?.[segmentIndex] || 'unknown';
		if (activeCategoryFilter && category !== activeCategoryFilter) {
			continue;
		}

		hitPositions.push(localHit.x, localHit.y, 0.01);
		const colorOffset = segmentIndex * 3;
		const colorTuple: [number, number, number] =
			raySegmentColors && colorOffset + 2 < raySegmentColors.length
				? [raySegmentColors[colorOffset], raySegmentColors[colorOffset + 1], raySegmentColors[colorOffset + 2]]
				: [1, 1, 1];
		hitColors.push(...colorTuple);

		const hitDistance = start.distanceTo(end);
		totalHitDistance += hitDistance;
		const categoryAccumulator = getCategoryAccumulator(
			categoryCounts,
			category,
			toRgbString(colorTuple[0], colorTuple[1], colorTuple[2])
		);
		categoryAccumulator.count += 1;
		categoryAccumulator.totalDistance += hitDistance;
	}

	// Project every ray (as infinite ray) onto the plane for center-bias-free heatmap.
	const planeRayDetails: PlaneRayDetail[] = [];
	for (let i = 0; i < raySegments.length; i += 6) {
		const segmentIndex = i / 6;
		start.set(raySegments[i], raySegments[i + 1], raySegments[i + 2]);
		end.set(raySegments[i + 3], raySegments[i + 4], raySegments[i + 5]);
		const category = raySegmentCategories?.[segmentIndex] || 'unknown';
		if (activeCategoryFilter && category !== activeCategoryFilter) {
			continue;
		}
		rayDir.subVectors(end, start).normalize();
		rayObj.set(start, rayDir);
		if (!rayObj.intersectPlane(plane, infiniteHit)) {
			continue;
		}
		infiniteLocalHit.copy(infiniteHit);
		planeMesh.worldToLocal(infiniteLocalHit);
		if (Math.abs(infiniteLocalHit.x) > halfW || Math.abs(infiniteLocalHit.y) > halfH) {
			continue;
		}
		const hitDistance = start.distanceTo(end);
		const distanceToPlane = Math.max(0.001, start.distanceTo(infiniteHit));
		const blockedBeforePlane = hitDistance < distanceToPlane;

		let blockedDistanceScore = 0;
		if (blockedBeforePlane) {
			if (activeBlockingMode === 'source') {
				blockedDistanceScore = THREE.MathUtils.clamp(
					hitDistance / HEATMAP_BLOCK_FALLOFF_METERS,
					0,
					1
				);
			} else {
				const distanceFromBlockerToPlane = Math.max(0, distanceToPlane - hitDistance);
				blockedDistanceScore = THREE.MathUtils.clamp(
					distanceFromBlockerToPlane / HEATMAP_BLOCK_FALLOFF_METERS,
					0,
					1
				);
			}
		}

		allRayPlaneData.push(
			infiniteLocalHit.x,
			infiniteLocalHit.y,
			blockedBeforePlane ? 1 : 0,
			blockedDistanceScore
		);
		const colorOffset = segmentIndex * 3;
		const cssColor =
			raySegmentColors && colorOffset + 2 < raySegmentColors.length
				? toRgbString(
						raySegmentColors[colorOffset],
						raySegmentColors[colorOffset + 1],
						raySegmentColors[colorOffset + 2]
					)
				: 'rgb(128,128,128)';
		planeRayDetails.push({
			localX: infiniteLocalHit.x,
			localY: infiniteLocalHit.y,
			category,
			cssColor,
			hitDistance,
			blocked: blockedBeforePlane,
			distanceScore: blockedDistanceScore,
		});
	}

	planeMesh.userData.planeRayDetails = planeRayDetails;
	planeMesh.userData.currentWidth = width;
	planeMesh.userData.currentHeight = height;

	if (points.geometry) {
		points.geometry.dispose();
	}
	const pointsGeometry = new THREE.BufferGeometry();
	if (hitPositions.length > 0) {
		pointsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(hitPositions, 3));
		pointsGeometry.setAttribute('color', new THREE.Float32BufferAttribute(hitColors, 3));
	}
	points.geometry = pointsGeometry;
	updateHeatmapTexture(heatmap, width, height, allRayPlaneData);
	applyPlaneVisualizationMode(planeMesh);

	const totalIntersections = hitPositions.length / 3;
	const categories = Array.from(categoryCounts.entries())
		.map(([category, value]) => ({
			category,
			count: value.count,
			percentage: totalIntersections > 0 ? (value.count / totalIntersections) * 100 : 0,
			color: value.color,
			averageDistance: value.count > 0 ? value.totalDistance / value.count : 0,
		}))
		.sort((a, b) => b.count - a.count);

	const windowId = planeMesh.userData.windowId as string | undefined;
	const stats: SelectedViewPlaneStats | null = windowId
		? {
				windowId,
				totalIntersections,
				planeDistance: forwardDistance,
				width,
				height,
				averageDistance: totalIntersections > 0 ? totalHitDistance / totalIntersections : 0,
				categories,
			}
		: null;

	planeMesh.userData.currentStats = stats;
};

const createOrUpdateSamplingPlane = (
	windowId: string,
	scene: THREE.Scene,
	origin: THREE.Vector3,
	viewDirection: THREE.Vector3,
	raySegments: number[],
	raySegmentColors?: number[],
	raySegmentCategories?: string[]
) => {
	clearSamplingPlanesExcept(windowId, scene);

	if (viewSamplingPlaneMap.has(windowId)) {
		const existingPlane = viewSamplingPlaneMap.get(windowId)!;
		existingPlane.userData.raySegments = raySegments;
		existingPlane.userData.raySegmentColors = raySegmentColors || [];
		existingPlane.userData.raySegmentCategories = raySegmentCategories || [];
		existingPlane.userData.blockingMode = useThreeSceneStore.getState().viewPlaneBlockingMode;
		existingPlane.userData.origin = origin.clone();
		updateSamplingIntersections(
			existingPlane,
			origin,
			raySegments,
			raySegmentColors,
			raySegmentCategories
		);
		return;
	}

	const forward = viewDirection.clone();
	forward.z = 0;
	if (forward.lengthSq() < 0.0001) {
		forward.set(1, 0, 0);
	}
	forward.normalize();

	const up = new THREE.Vector3(0, 0, 1);
	const right = new THREE.Vector3().crossVectors(forward, up).normalize();
	const planeFacing = forward.clone().negate();
	const orientation = new THREE.Matrix4().makeBasis(right, up, planeFacing);
	const rotation = new THREE.Quaternion().setFromRotationMatrix(orientation);

	const planeMesh = new THREE.Mesh(
		new THREE.PlaneGeometry(1, 1),
		new THREE.MeshBasicMaterial({
			color: 0x0ea5e9,
			transparent: true,
			opacity: 0.14,
			side: THREE.DoubleSide,
			depthWrite: false,
		})
	);
	planeMesh.name = `__viewRaySamplingPlane__${windowId}`;
	planeMesh.position.copy(origin.clone().add(forward.clone().multiplyScalar(DEFAULT_PLANE_DISTANCE)));
	planeMesh.quaternion.copy(rotation);
	planeMesh.renderOrder = 10;
	planeMesh.userData.isViewRaySamplingPlane = true;
	planeMesh.userData.ignoreSceneClick = true;
	planeMesh.userData.windowId = windowId;
	planeMesh.userData.raySegments = raySegments;
	planeMesh.userData.raySegmentColors = raySegmentColors || [];
	planeMesh.userData.raySegmentCategories = raySegmentCategories || [];
	planeMesh.userData.origin = origin.clone();
	planeMesh.userData.baseForward = forward.clone();
	planeMesh.userData.categoryFilter = useThreeSceneStore.getState().viewPlaneCategoryFilter;
	planeMesh.userData.blockingMode = useThreeSceneStore.getState().viewPlaneBlockingMode;

	const frame = new THREE.LineSegments(
		new THREE.BufferGeometry(),
		new THREE.LineBasicMaterial({
			color: 0x38bdf8,
			transparent: true,
			opacity: 0.85,
			depthWrite: false,
		})
	);
	frame.name = '__viewRayPlaneFrame__';
	frame.userData.ignoreSceneClick = true;
	frame.renderOrder = 11;

	const points = new THREE.Points(
		new THREE.BufferGeometry(),
		new THREE.PointsMaterial({
			vertexColors: true,
			size: 0.32,
			sizeAttenuation: true,
			transparent: true,
			opacity: 1,
			depthTest: false,
			depthWrite: false,
		})
	);
	points.name = '__viewRayPlanePoints__';
	points.userData.ignoreSceneClick = true;
	points.renderOrder = 12;

	const heatmapCanvas = document.createElement('canvas');
	heatmapCanvas.width = 240;
	heatmapCanvas.height = 180;
	const heatmapTexture = new THREE.CanvasTexture(heatmapCanvas);
	heatmapTexture.wrapS = THREE.ClampToEdgeWrapping;
	heatmapTexture.wrapT = THREE.ClampToEdgeWrapping;
	const heatmap = new THREE.Mesh(
		new THREE.PlaneGeometry(1, 1),
		new THREE.MeshBasicMaterial({
			map: heatmapTexture,
			transparent: true,
		opacity: 1,
		depthTest: false,
		depthWrite: false,
		blending: THREE.NormalBlending,
			side: THREE.DoubleSide,
		})
	);
	heatmap.name = '__viewRayPlaneHeatmap__';
	heatmap.userData.ignoreSceneClick = true;
	heatmap.userData.canvas = heatmapCanvas;
	heatmap.userData.texture = heatmapTexture;
	heatmap.position.z = 0.005;
	heatmap.renderOrder = 11;

	planeMesh.add(frame);
	planeMesh.add(heatmap);
	planeMesh.add(points);
	scene.add(planeMesh);
	viewSamplingPlaneMap.set(windowId, planeMesh);

	updateSamplingIntersections(
		planeMesh,
		origin,
		raySegments,
		raySegmentColors,
		raySegmentCategories
	);
	applyPlaneVisualizationMode(planeMesh);
};

export const updateSamplingPlaneFromTransform = (planeObject: THREE.Object3D) => {
	let mesh: THREE.Mesh | null = null;

	if (planeObject instanceof THREE.Mesh && planeObject.userData.isViewRaySamplingPlane) {
		mesh = planeObject;
	} else {
		planeObject.traverse((child) => {
			if (!mesh && child instanceof THREE.Mesh && child.userData.isViewRaySamplingPlane) {
				mesh = child;
			}
		});
	}

	if (!(mesh instanceof THREE.Mesh) || !mesh.userData.isViewRaySamplingPlane) {
		return;
	}

	const originData = mesh.userData.origin;
	const raySegmentsData = mesh.userData.raySegments;
	const raySegmentColorsData = mesh.userData.raySegmentColors;
	const raySegmentCategoriesData = mesh.userData.raySegmentCategories;
	if (!originData || !Array.isArray(raySegmentsData)) {
		return;
	}

	const origin = originData instanceof THREE.Vector3
		? originData
		: new THREE.Vector3(originData.x, originData.y, originData.z);

	updateSamplingIntersections(
		mesh,
		origin,
		raySegmentsData,
		Array.isArray(raySegmentColorsData) ? raySegmentColorsData : undefined,
		Array.isArray(raySegmentCategoriesData) ? raySegmentCategoriesData : undefined
	);
};

export const getSamplingPlaneStats = (planeObject: THREE.Object3D): SelectedViewPlaneStats | null => {
	let mesh: THREE.Mesh | null = null;

	if (planeObject instanceof THREE.Mesh && planeObject.userData.isViewRaySamplingPlane) {
		mesh = planeObject;
	} else {
		planeObject.traverse((child) => {
			if (!mesh && child instanceof THREE.Mesh && child.userData.isViewRaySamplingPlane) {
				mesh = child;
			}
		});
	}

	if (!(mesh instanceof THREE.Mesh)) {
		return null;
	}

	return (mesh.userData.currentStats as SelectedViewPlaneStats | null) || null;
};

export const queryRaysAtLocalPoint = (
	planeMesh: THREE.Object3D,
	localX: number,
	localY: number,
	scale = 1
): PlaneRayDetail[] => {
	const details = planeMesh.userData.planeRayDetails as PlaneRayDetail[] | undefined;
	if (!details || details.length === 0) return [];
	const planeWidth = (planeMesh.userData.currentWidth as number | undefined) ?? 20;
	const planeHeight = (planeMesh.userData.currentHeight as number | undefined) ?? 10;
	const radius = Math.max(0.5, Math.max(planeWidth, planeHeight) * 0.07 * Math.max(0.5, Math.min(2, scale)));
	const radiusSq = radius * radius;
	return details.filter((d) => {
		const dx = d.localX - localX;
		const dy = d.localY - localY;
		return dx * dx + dy * dy <= radiusSq;
	});
};

export const setSamplingPlaneVisualizationMode = (mode: ViewPlaneVisualizationMode) => {
	viewSamplingPlaneMap.forEach((planeMesh) => {
		applyPlaneVisualizationMode(planeMesh, mode);
	});
};

export const setSamplingPlaneCategoryFilter = (category: string | null) => {
	viewSamplingPlaneMap.forEach((planeMesh) => {
		planeMesh.userData.categoryFilter = category;
		const originData = planeMesh.userData.origin;
		const raySegmentsData = planeMesh.userData.raySegments;
		const raySegmentColorsData = planeMesh.userData.raySegmentColors;
		const raySegmentCategoriesData = planeMesh.userData.raySegmentCategories;
		if (!originData || !Array.isArray(raySegmentsData)) {
			return;
		}

		const origin = originData instanceof THREE.Vector3
			? originData
			: new THREE.Vector3(originData.x, originData.y, originData.z);

		updateSamplingIntersections(
			planeMesh,
			origin,
			raySegmentsData,
			Array.isArray(raySegmentColorsData) ? raySegmentColorsData : undefined,
			Array.isArray(raySegmentCategoriesData) ? raySegmentCategoriesData : undefined
		);
	});
};

export const setSamplingPlaneBlockingMode = (mode: ViewPlaneBlockingMode) => {
	viewSamplingPlaneMap.forEach((planeMesh) => {
		planeMesh.userData.blockingMode = mode;
		const originData = planeMesh.userData.origin;
		const raySegmentsData = planeMesh.userData.raySegments;
		const raySegmentColorsData = planeMesh.userData.raySegmentColors;
		const raySegmentCategoriesData = planeMesh.userData.raySegmentCategories;
		if (!originData || !Array.isArray(raySegmentsData)) {
			return;
		}

		const origin = originData instanceof THREE.Vector3
			? originData
			: new THREE.Vector3(originData.x, originData.y, originData.z);

		updateSamplingIntersections(
			planeMesh,
			origin,
			raySegmentsData,
			Array.isArray(raySegmentColorsData) ? raySegmentColorsData : undefined,
			Array.isArray(raySegmentCategoriesData) ? raySegmentCategoriesData : undefined
		);
	});
};

export const performScan = async (params: PerformScanParams): Promise<void> => {
	const {
		scene,
		camera,
		originPoint,
		viewDirection,
		targets,
		setDebugGeo,
		setAnalysisData,
		categoryFilter,
		onProgress,
		windowId,
	} = params;

	if (!targets.length) {
		console.warn('No targets provided for scan');
		return;
	}

	const startPos = originPoint || camera.position.clone();

	// Force update ALL world matrices BEFORE traversal
	targets.forEach((target) => {
		target.updateMatrixWorld(true);
	});
	scene.updateMatrixWorld(true);

	// Serialize all meshes
	const serializedMeshes: SerializedMesh[] = [];
	targets.forEach((target) => {
		target.traverse((obj) => {
			if (obj instanceof THREE.Mesh && obj.visible && obj.geometry) {
				const isWireframe =
					obj.userData.isEdgeWireframe ||
					obj instanceof THREE.LineSegments ||
					obj instanceof THREE.Line;
				const isRoomFloor = obj.userData.isRoomFloorFill || obj.userData.isRoomFloorOutline;
				const isViewRayPlane = obj.userData.isViewRaySamplingPlane;

				if (!isWireframe && !isRoomFloor && !isViewRayPlane) {
					const serialized = serializeMesh(obj);
					serializedMeshes.push(serialized);
				}
			}
		});
	});

	// Prepare view direction (Z-up coordinate system)
	const viewDir = viewDirection?.clone() || new THREE.Vector3();
	if (!viewDirection) {
		camera.getWorldDirection(viewDir);
	}

	return new Promise<void>((resolve, reject) => {
		const worker = new Worker(new URL('../workers/viewAnalysis.worker.ts', import.meta.url), {
			type: 'module',
		});

		worker.onmessage = (e: MessageEvent) => {
			const data = e.data as {
				type: 'progress' | 'complete';
				progress?: number;
				positions?: number[];
				colors?: number[];
				raySegments?: number[];
				raySegmentColors?: number[];
				raySegmentCategories?: string[];
				statsMap?: Record<string, StatsMap>;
			};

			if (data.type === 'progress') {
				if (onProgress && data.progress !== undefined) {
					onProgress(data.progress);
				}
			} else if (data.type === 'complete') {
				// Remove existing lines for this window if windowId provided
				if (windowId && viewLinesMap.has(windowId)) {
					const existingLines = viewLinesMap.get(windowId)!;
					scene.remove(existingLines);
					existingLines.geometry.dispose();
					if (existingLines.material instanceof THREE.Material) {
						existingLines.material.dispose();
					} else if (Array.isArray(existingLines.material)) {
						existingLines.material.forEach((mat) => mat.dispose());
					}
					viewLinesMap.delete(windowId);
				}

				// Note: do NOT pre-remove the existing sampling plane here.
				// createOrUpdateSamplingPlane handles the update-in-place path,
				// preserving any user-dragged position.

				if (data.positions && data.colors) {
					const newGeometry = new THREE.BufferGeometry();
					newGeometry.setAttribute(
						'position',
						new THREE.Float32BufferAttribute(data.positions, 3)
					);
					newGeometry.setAttribute(
						'color',
						new THREE.Float32BufferAttribute(data.colors, 3)
					);

					const material = new THREE.LineBasicMaterial({ vertexColors: true });
					const lines = new THREE.LineSegments(newGeometry, material);
					lines.name = windowId ? `viewScanDebugGeo_${windowId}` : 'viewScanDebugGeo';
					scene.add(lines);

					// Store lines with windowId if provided
					if (windowId) {
						viewLinesMap.set(windowId, lines);
					}

					setDebugGeo(newGeometry);
				}

				if (windowId && data.raySegments && data.raySegments.length > 0) {
					createOrUpdateSamplingPlane(
						windowId,
						scene,
						startPos.clone(),
						viewDir.clone(),
						data.raySegments,
						data.raySegmentColors,
						data.raySegmentCategories
					);
				}

				if (data.statsMap) {
					setAnalysisData(data.statsMap);
				}

				worker.terminate();
				resolve();
			}
		};

		worker.onerror = (error) => {
			console.error('View analysis worker error:', error);
			worker.terminate();
			reject(error);
		};

		// Serialize painted areas from store
		const paintedAreas = useViewFeaturesStore.getState().paintedAreas;
		const serializedPaintedAreas: SerializedPaintedArea[] = paintedAreas
			.filter((area) => area.type !== 'none')
			.map((area) => ({
				id: area.id,
				type: area.type as 'water' | 'greenery' | 'park',
				points: area.points.map((p) => ({ x: p.x, y: p.y, z: p.z })),
			}));

		// Send data to worker
		worker.postMessage({
			originPoint: { x: startPos.x, y: startPos.y, z: startPos.z },
			viewDirection: { x: viewDir.x, y: viewDir.y, z: viewDir.z },
			meshes: serializedMeshes,
			paintedAreas: serializedPaintedAreas.length > 0 ? serializedPaintedAreas : undefined,
			categoryFilter: categoryFilter ? Array.from(categoryFilter) : undefined,
			visualizeEveryNth: 100,
		});
	});
};

export const clearViewLines = (scene: THREE.Scene) => {
	useThreeSceneStore.getState().setSelectedViewPlaneStats(null);
	if (typeof window !== 'undefined') {
		window.dispatchEvent(new CustomEvent('__viewAnalysisClearVisualization__'));
	}
	// Clear all stored view lines
	viewLinesMap.forEach((lines) => {
		scene.remove(lines);
		lines.geometry.dispose();
		if (lines.material instanceof THREE.Material) {
			lines.material.dispose();
		} else if (Array.isArray(lines.material)) {
			lines.material.forEach((mat) => mat.dispose());
		}
	});
	viewLinesMap.clear();

	viewSamplingPlaneMap.forEach((plane) => {
		scene.remove(plane);
		disposeObject3D(plane);
	});
	viewSamplingPlaneMap.clear();

	// Also remove any legacy view lines without windowId
	const legacyLines = scene.children.filter(
		(obj) => obj.name === 'viewScanDebugGeo' && obj instanceof THREE.LineSegments
	);
	legacyLines.forEach((lines) => {
		scene.remove(lines);
		if (lines instanceof THREE.LineSegments) {
			lines.geometry.dispose();
			if (lines.material instanceof THREE.Material) {
				lines.material.dispose();
			} else if (Array.isArray(lines.material)) {
				lines.material.forEach((mat) => mat.dispose());
			}
		}
	});
};

export const removeViewLineById = (windowId: string, scene: THREE.Scene) => {
	if (viewSamplingPlaneMap.has(windowId)) {
		const plane = viewSamplingPlaneMap.get(windowId)!;
		if (useThreeSceneStore.getState().selectedViewPlaneStats?.windowId === windowId) {
			useThreeSceneStore.getState().setSelectedViewPlaneStats(null);
		}
		scene.remove(plane);
		disposeObject3D(plane);
		viewSamplingPlaneMap.delete(windowId);
	}

	if (viewLinesMap.has(windowId)) {
		const lines = viewLinesMap.get(windowId)!;
		scene.remove(lines);
		lines.geometry.dispose();
		if (lines.material instanceof THREE.Material) {
			lines.material.dispose();
		} else if (Array.isArray(lines.material)) {
			lines.material.forEach((mat) => mat.dispose());
		}
		viewLinesMap.delete(windowId);
		return true;
	}
	return false;
};
